import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand Column */}
          <motion.div 
            className="md:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/" className="inline-block mb-6">
              <span className="font-heading text-3xl font-medium text-white">
                Easy<span className="text-sage">Coaching</span>
              </span>
            </Link>
            <p className="font-body text-gray-400 leading-relaxed max-w-sm mb-8">
              Accompagnement premium en santé, énergie et performance durable. 
              Une méthodologie de transformation globale pour des résultats concrets.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:contact@easycoaching.be"
                data-testid="footer-email"
                className="flex items-center gap-3 text-gray-400 hover:text-sage transition-colors group"
              >
                <Mail size={18} className="text-sage" />
                <span className="font-body">contact@easycoaching.be</span>
              </a>
              <a
                href="tel:+32472802732"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-gray-400 hover:text-sage transition-colors group"
              >
                <Phone size={18} className="text-sage" />
                <span className="font-body">+32 472 80 27 32</span>
              </a>
              <div className="flex items-center gap-3 text-gray-400">
                <MapPin size={18} className="text-sage" />
                <span className="font-body">Belgique</span>
              </div>
            </div>
          </motion.div>

          {/* Navigation Column */}
          <motion.div 
            className="md:col-span-3"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="font-heading text-lg text-white mb-6">Navigation</h4>
            <ul className="space-y-4">
              {[
                { href: '/', label: 'Accueil' },
                { href: '/particuliers', label: 'Particuliers' },
                { href: '/entreprises', label: 'Entreprises' },
                { href: '/contact', label: 'Contact' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="font-body text-gray-400 hover:text-white transition-colors inline-flex items-center gap-2 group"
                  >
                    {link.label}
                    <ArrowUpRight
                      size={14}
                      className="opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* CTA Column */}
          <motion.div 
            className="md:col-span-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="font-heading text-lg text-white mb-6">
              Prêt(e) à vous engager ?
            </h4>
            <p className="font-body text-gray-400 mb-6 leading-relaxed">
              Un accompagnement demande un réel engagement – des deux côtés.
              Les places sont limitées.
            </p>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/contact"
                data-testid="footer-cta-button"
                className="inline-flex items-center gap-2 bg-sage text-black font-body font-semibold px-6 py-3 rounded-full hover:bg-sage-300 transition-all duration-300"
              >
                Planifier un échange
                <ArrowUpRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="font-body text-sm text-gray-500">
            © {currentYear} EasyCoaching. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6">
            <span className="font-body text-xs text-gray-600 uppercase tracking-wider">
              Engagement
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
            <span className="font-body text-xs text-gray-600 uppercase tracking-wider">
              Durabilité
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
            <span className="font-body text-xs text-gray-600 uppercase tracking-wider">
              Résultats
            </span>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
